import { useQuery } from '@tanstack/react-query';
import { XCircleIcon } from 'lucide-react';

import { api } from '@/lib/api';
import { useContent, useLabel, useT, useTn } from '@/lib/i18n';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Question Types — the human view of the item-type registry (build/09 §4).
 *
 * The point of this screen is that item types are DATA, not a TypeScript enum:
 * the contract validates `itemType` as a format-checked string against a live
 * registry, so a new type appears without a deployment. This screen is where a
 * reader can see that claim is true.
 *
 * Everything here is derived from what the platform already knows — which
 * modules declare each type, and how many items of it exist, in one aggregation
 * rather than a count per type.
 *
 * WHAT IS NOT BUILT: `09 §4` also wants an enable/disable toggle gating which
 * types are offered for new authoring. That needs a persisted set of enabled
 * types, and no such collection exists — so there is no toggle here rather than
 * a switch that appears to do something. Said on the screen, not just here.
 */

/**
 * The launch set (contract `LAUNCH_ITEM_TYPES`).
 *
 * The notes about each type — what a candidate does, and how it scores — moved
 * into the string catalogue as `questionTypes.answer.<type>` and
 * `questionTypes.scoring.<type>`, looked up through `useLabel`. What stays here
 * is the LIST, because that is structure rather than copy, and a type present in
 * the bank but not in this list still renders (which is the whole point of an
 * open registry).
 */
const LAUNCH_TYPES = ['likert', 'slider', 'forced_choice', 'ranking', 'mcq', 'sjt', 'bars'];

export function QuestionTypes() {
  const t = useT();
  const tn = useTn();
  const label = useLabel();
  const content = useContent();
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });
  const stats = useQuery({ queryKey: ['question-stats'], queryFn: api.questionStats });

  const isLoading = modules.isLoading || stats.isLoading;
  const error = modules.error ?? stats.error;

  const countsByType = new Map((stats.data?.items ?? []).map((s) => [s.itemType, s]));

  // Types the registry knows, plus any the bank actually contains — a type that
  // appears in data but not in this file is exactly the case the open registry
  // is designed to allow, and it must not be invisible here.
  const known = [...new Set([...LAUNCH_TYPES, ...countsByType.keys()])];

  const modulesUsing = (itemType: string) =>
    (modules.data?.items ?? []).filter((m) => m.supportedItemTypes?.includes(itemType));

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('nav.questions.types')}
        description={t('questionTypes.description')}
        actions={stats.data && <Badge variant="secondary">{tn('questionTypes.count', known.length)}</Badge>}
      />

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      )}

      {error && (
        <EmptyState
          icon={XCircleIcon}
          title={t('questionTypes.loadFailed')}
          description={error instanceof Error ? error.message : t('table.errorDescription')}
        />
      )}

      {!isLoading && !error && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {known.map((itemType) => {
              const counts = countsByType.get(itemType);
              const users = modulesUsing(itemType);
              // `useLabel` returns the raw value when there is no entry, which is
              // how an undescribed type is detected — see the open-registry note
              // at the foot of this screen.
              const answer = label('questionTypes.answer', itemType);
              const scoring = label('questionTypes.scoring', itemType);

              return (
                <Card key={itemType}>
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="font-mono text-base" dir="ltr">{itemType}</CardTitle>
                      <Badge variant={counts?.certified ? 'default' : 'outline'}>
                        {t('questionTypes.certifiedCount', { count: counts?.certified ?? 0 })}
                      </Badge>
                    </div>
                    <CardDescription>
                      {answer === itemType ? t('questionTypes.undescribed') : answer}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <p className="text-muted-foreground">
                      {scoring === itemType ? t('questionTypes.scoredByPlugin') : scoring}
                    </p>

                    <div>
                      <p className="text-muted-foreground text-xs">{t('questionTypes.usedBy')}</p>
                      {users.length === 0 ? (
                        <p className="text-muted-foreground">{t('questionTypes.unused')}</p>
                      ) : (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {users.map((m) => (
                            <Badge key={m.moduleId} variant="secondary">
                              {content(m.name)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {counts && counts.total !== counts.certified && (
                      <p className="text-muted-foreground text-xs">
                        {t('questionTypes.breakdown', {
                          total: counts.total,
                          draft: counts.draft,
                          retired: counts.retired,
                        })}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* The open-registry explanation went with the other implementation
              footnotes; what an operator can ACT on stays — that no toggle
              exists is a fact about the screen, not about the pipeline. */}
          <p className="text-muted-foreground mt-6 text-sm">{t('questionTypes.noToggle')}</p>
        </>
      )}
    </div>
  );
}
